import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Save, Shield, Clock, Calendar, Hash, Zap, Timer, Globe } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

const DAYS = [
  { value: 0, label: "Lun" },
  { value: 1, label: "Mar" },
  { value: 2, label: "Mie" },
  { value: 3, label: "Jue" },
  { value: 4, label: "Vie" },
  { value: 5, label: "Sab" },
  { value: 6, label: "Dom" },
];

const TIMEZONES = [
  { value: "America/Argentina/Buenos_Aires", label: "Argentina (GMT-3)" },
  { value: "America/Santiago", label: "Chile (GMT-4)" },
  { value: "America/Bogota", label: "Colombia (GMT-5)" },
  { value: "America/Mexico_City", label: "Mexico (GMT-6)" },
  { value: "America/Lima", label: "Peru (GMT-5)" },
  { value: "America/Caracas", label: "Venezuela (GMT-4)" },
  { value: "America/Montevideo", label: "Uruguay (GMT-3)" },
  { value: "America/Asuncion", label: "Paraguay (GMT-4)" },
  { value: "America/La_Paz", label: "Bolivia (GMT-4)" },
  { value: "America/Guayaquil", label: "Ecuador (GMT-5)" },
  { value: "America/Sao_Paulo", label: "Brasil (GMT-3)" },
  { value: "Europe/Madrid", label: "Espana (GMT+1)" },
  { value: "US/Eastern", label: "EEUU Este (GMT-5)" },
  { value: "US/Central", label: "EEUU Centro (GMT-6)" },
  { value: "US/Pacific", label: "EEUU Pacifico (GMT-8)" },
  { value: "UTC", label: "UTC (GMT+0)" },
];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      const data = res.data;
      if (!data.active_hours || data.active_hours.length === 0) {
        data.active_hours = [{ start: "08:00", end: "21:00" }];
      }
      setSettings(data);
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, settings);
      setSettings(res.data);
      toast.success("Configuracion guardada");
    } catch (e) {
      toast.error("Error al guardar configuracion");
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    const days = settings.active_days || [];
    if (days.includes(day)) {
      setSettings({ ...settings, active_days: days.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, active_days: [...days, day].sort() });
    }
  };

  const addHourWindow = () => {
    const hours = [...(settings.active_hours || []), { start: "08:00", end: "21:00" }];
    setSettings({ ...settings, active_hours: hours });
  };

  const removeHourWindow = (idx) => {
    const hours = (settings.active_hours || []).filter((_, i) => i !== idx);
    setSettings({ ...settings, active_hours: hours.length > 0 ? hours : [{ start: "00:00", end: "23:59" }] });
  };

  const updateHourWindow = (idx, field, value) => {
    const hours = [...(settings.active_hours || [])];
    hours[idx] = { ...hours[idx], [field]: value };
    setSettings({ ...settings, active_hours: hours });
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">Seguridad Anti-Ban</h1>
          <p className="text-sm text-zinc-500 mt-1">Configura los parametros de proteccion para evitar bloqueos</p>
        </div>
        <button
          data-testid="save-settings-btn"
          onClick={saveSettings}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)] rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200 active:scale-95 disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delay Configuration */}
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-5 space-y-5">
          <div className="flex items-center gap-3 pb-3 border-b border-white/5">
            <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center">
              <Clock className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Delay entre Envios</h3>
              <p className="text-[10px] text-zinc-500">Tiempo de espera aleatorio entre cada mensaje</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Minimo (seg)</label>
              <input
                data-testid="delay-min-input"
                type="number"
                value={settings.delay_min}
                onChange={(e) => setSettings({ ...settings, delay_min: parseInt(e.target.value) || 0 })}
                className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white font-mono outline-none transition-colors duration-200"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Maximo (seg)</label>
              <input
                data-testid="delay-max-input"
                type="number"
                value={settings.delay_max}
                onChange={(e) => setSettings({ ...settings, delay_max: parseInt(e.target.value) || 0 })}
                className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white font-mono outline-none transition-colors duration-200"
              />
            </div>
          </div>
          <div className="bg-zinc-950/50 rounded-md p-3">
            <p className="text-[10px] font-mono text-zinc-500">
              Rango: <span className="text-cyan-400">{settings.delay_min}s - {settings.delay_max}s</span>
            </p>
          </div>
        </div>

        {/* Daily Limit */}
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-5 space-y-5">
          <div className="flex items-center gap-3 pb-3 border-b border-white/5">
            <div className="w-8 h-8 rounded-lg bg-amber-600/10 flex items-center justify-center">
              <Shield className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Limite Diario</h3>
              <p className="text-[10px] text-zinc-500">Maximo de mensajes por perfil por dia</p>
            </div>
          </div>
          <input
            data-testid="daily-limit-input"
            type="number"
            value={settings.daily_limit}
            onChange={(e) => setSettings({ ...settings, daily_limit: parseInt(e.target.value) || 0 })}
            className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white font-mono outline-none transition-colors duration-200"
          />
          <div className="bg-zinc-950/50 rounded-md p-3">
            <p className="text-[10px] font-mono text-zinc-500">
              Limite: <span className="text-amber-400">{settings.daily_limit}</span> mensajes/dia por perfil
            </p>
          </div>
        </div>

        {/* Rest Period */}
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-5 space-y-5">
          <div className="flex items-center gap-3 pb-3 border-b border-white/5">
            <div className="w-8 h-8 rounded-lg bg-emerald-600/10 flex items-center justify-center">
              <Zap className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Periodo de Descanso</h3>
              <p className="text-[10px] text-zinc-500">Pausa automatica despues de X mensajes</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Cada (mensajes)</label>
              <input
                data-testid="rest-after-input"
                type="number"
                value={settings.rest_after_count}
                onChange={(e) => setSettings({ ...settings, rest_after_count: parseInt(e.target.value) || 0 })}
                className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white font-mono outline-none transition-colors duration-200"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Duracion (seg)</label>
              <input
                data-testid="rest-duration-input"
                type="number"
                value={settings.rest_duration}
                onChange={(e) => setSettings({ ...settings, rest_duration: parseInt(e.target.value) || 0 })}
                className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white font-mono outline-none transition-colors duration-200"
              />
            </div>
          </div>
          <div className="bg-zinc-950/50 rounded-md p-3">
            <p className="text-[10px] font-mono text-zinc-500">
              Descanso de <span className="text-emerald-400">{settings.rest_duration}s</span> cada <span className="text-emerald-400">{settings.rest_after_count}</span> mensajes
            </p>
          </div>
        </div>

        {/* Active Days */}
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-5 space-y-5">
          <div className="flex items-center gap-3 pb-3 border-b border-white/5">
            <div className="w-8 h-8 rounded-lg bg-cyan-600/10 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Dias Activos</h3>
              <p className="text-[10px] text-zinc-500">Dias de la semana en que se permite el envio</p>
            </div>
          </div>
          <div className="flex gap-2">
            {DAYS.map(d => (
              <button
                key={d.value}
                data-testid={`day-toggle-${d.value}`}
                onClick={() => toggleDay(d.value)}
                className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors duration-200 ${
                  (settings.active_days || []).includes(d.value)
                    ? 'bg-blue-600 text-white'
                    : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Timezone */}
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-5 space-y-5">
          <div className="flex items-center gap-3 pb-3 border-b border-white/5">
            <div className="w-8 h-8 rounded-lg bg-indigo-600/10 flex items-center justify-center">
              <Globe className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Zona Horaria</h3>
              <p className="text-[10px] text-zinc-500">Los horarios de envio se calculan segun esta zona</p>
            </div>
          </div>
          <select
            data-testid="timezone-select"
            value={settings.timezone || "America/Argentina/Buenos_Aires"}
            onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
            className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white font-mono outline-none transition-colors duration-200 [color-scheme:dark]"
          >
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          <div className="bg-zinc-950/50 rounded-md p-3">
            <p className="text-[10px] font-mono text-zinc-500">
              Zona: <span className="text-indigo-400">{settings.timezone || "America/Argentina/Buenos_Aires"}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Active Hours */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg p-5 space-y-5">
        <div className="flex items-center justify-between pb-3 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-600/10 flex items-center justify-center">
              <Timer className="w-4 h-4 text-violet-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Horarios de Envio</h3>
              <p className="text-[10px] text-zinc-500">Ventanas horarias permitidas (puedes agregar multiples rangos)</p>
            </div>
          </div>
          <button
            data-testid="add-hour-window-btn"
            onClick={addHourWindow}
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 rounded-md px-3 py-1.5 text-xs font-medium transition-colors duration-200"
          >
            + Agregar Rango
          </button>
        </div>

        <div className="space-y-3">
          {(settings.active_hours || []).map((window, idx) => (
            <div key={idx} data-testid={`hour-window-${idx}`} className="flex items-center gap-4 bg-zinc-950/50 rounded-lg p-4">
              <span className="text-[10px] font-mono text-zinc-600 uppercase w-16">Rango {idx + 1}</span>
              <div className="flex items-center gap-2">
                <label className="text-[10px] font-mono text-zinc-500">Desde</label>
                <input
                  data-testid={`hour-start-${idx}`}
                  type="time"
                  value={window.start}
                  onChange={(e) => updateHourWindow(idx, "start", e.target.value)}
                  className="bg-zinc-900 border border-zinc-800 focus:border-blue-500 rounded-md px-2 py-1.5 text-sm text-white font-mono outline-none transition-colors duration-200 [color-scheme:dark]"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] font-mono text-zinc-500">Hasta</label>
                <input
                  data-testid={`hour-end-${idx}`}
                  type="time"
                  value={window.end}
                  onChange={(e) => updateHourWindow(idx, "end", e.target.value)}
                  className="bg-zinc-900 border border-zinc-800 focus:border-blue-500 rounded-md px-2 py-1.5 text-sm text-white font-mono outline-none transition-colors duration-200 [color-scheme:dark]"
                />
              </div>
              <div className="flex-1" />
              {(settings.active_hours || []).length > 1 && (
                <button
                  data-testid={`remove-hour-${idx}`}
                  onClick={() => removeHourWindow(idx)}
                  className="text-red-400 hover:text-red-300 text-xs transition-colors duration-200"
                >
                  Eliminar
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="bg-zinc-950/50 rounded-md p-3">
          <p className="text-[10px] font-mono text-zinc-500">
            Ejemplo: De <span className="text-violet-400">08:00</span> a <span className="text-violet-400">12:00</span> y de <span className="text-violet-400">14:00</span> a <span className="text-violet-400">21:00</span> = pausa del mediodia
          </p>
        </div>
      </div>

      {/* RCS Preference */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center">
              <Hash className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Priorizar RCS</h3>
              <p className="text-[10px] text-zinc-500">
                Si esta activo, la extension intentara enviar via RCS antes de SMS
              </p>
            </div>
          </div>
          <button
            data-testid="toggle-rcs-btn"
            onClick={() => setSettings({ ...settings, prefer_rcs: !settings.prefer_rcs })}
            className={`w-12 h-6 rounded-full transition-colors duration-200 relative ${
              settings.prefer_rcs ? 'bg-blue-600' : 'bg-zinc-700'
            }`}
          >
            <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-200 ${
              settings.prefer_rcs ? 'translate-x-6' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
      </div>
    </div>
  );
}
